import { useState } from "react";
import { Alert, Card, Col, Row, Select, Spin } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { AuditDto, ConfigDto, SupplierDto } from "@audit-plan/shared";
import { download } from "../api/client";
import { countDisabledEvents } from "@audit-plan/shared";
import { useAuth } from "../auth/AuthContext";
import { useConfig, useShowRisk } from "../api/plan";
import { useDashboard } from "../api/dashboard";
import { useSuppliers } from "../api/suppliers";
import { AuditStatusPill, Pill, Rating, RiskPill, fmtD, fmtN, fmtWhen } from "../components/pills";
import { HBar, RatingDistribution, TrendChart } from "../components/charts/Charts";
import { Kpi, PageHead, kpiGrid, kpiGridClass } from "../components/dashboard/Kpi";
import { OverdueActions } from "../components/dashboard/ActionsList";
import { ActionDrawer } from "../components/action/ActionDrawer";
import { ApiError } from "../api/client";
import { FilterBar, PageActions } from "../components/FilterBar";
import { ResponsiveList } from "../components/ResponsiveList";
import { useIsMobile } from "../components/useIsMobile";

export default function DashboardPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const mobile = useIsMobile();
  const { can } = useAuth();
  const cfg = useConfig();
  const showRisk = useShowRisk();
  const thisYear = dayjs().year();
  const [year, setYear] = useState<number | undefined>(thisYear);
  const [supplierId, setSupplierId] = useState<string>();
  const [risk, setRisk] = useState<string>();
  const [actionId, setActionId] = useState<string | null>(null);
  const sups = useSuppliers();
  const dash = useDashboard({ year, supplierId, risk });

  if (dash.isLoading) return <Spin style={{ display: "block", margin: 48 }} />;
  if (dash.error || !dash.data) return <Alert type="error" showIcon message={dash.error instanceof ApiError ? dash.error.message : t("a_loadFail")} />;
  const d = dash.data;
  const k = d.kpi;
  const years = [thisYear - 2, thisYear - 1, thisYear, thisYear + 1];
  const capRows = d.topRiskSuppliers.filter((s) => s.capPct != null).sort((a, b) => (a.capPct ?? 0) - (b.capPct ?? 0));

  return (
    <div>
      <PageHead
        title={t("dashboard")}
        actions={
          <>
            {can("admin") && cfg.data && <NotifBadge cfg={cfg.data.notifications} onClick={() => nav("/admin?tab=ntf")} />}
            <PageActions items={[{ key: "xlsx", label: t("exportXlsx"), icon: <DownloadOutlined />, onClick: () => void download("/exports/suppliers.xlsx") }]} />
          </>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <FilterBar
          style={{ marginBottom: 0, alignItems: "flex-end" }}
          active={[year !== thisYear, supplierId, risk].filter(Boolean).length}
          onReset={() => {
            setYear(thisYear);
            setSupplierId(undefined);
            setRisk(undefined);
          }}
        >
          <Field label={t("planYear")}>
            <Select style={{ width: 110 }} value={year ?? "all"} onChange={(v) => setYear(v === "all" ? undefined : Number(v))} options={[{ value: "all", label: t("all") }, ...years.map((y) => ({ value: y, label: String(y) }))]} />
          </Field>
          <Field label={t("supplier")}>
            <Select
              showSearch
              allowClear
              placeholder={t("all")}
              style={{ width: 240 }}
              value={supplierId}
              onChange={setSupplierId}
              optionFilterProp="label"
              options={(sups.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            />
          </Field>
          {showRisk && (
            <Field label={t("risk")}>
              <Select allowClear placeholder={t("all")} style={{ width: 150 }} value={risk} onChange={setRisk} options={["high", "medium", "low"].map((r) => ({ value: r, label: t(`risk_${r}`) }))} />
            </Field>
          )}
        </FilterBar>
      </Card>

      <div style={kpiGrid} className={kpiGridClass}>
        <Kpi value={k.auditsPlanned} label={t("plannedAudits")} onClick={() => nav("/audits")} />
        <Kpi value={k.auditsInProgress} label={t("a_auditsInProgress")} onClick={() => nav("/audits")} />
        <Kpi value={k.auditsClosed} label={t("completedAudits")} tone="good" onClick={() => nav("/audits")} />
        <Kpi value={k.openNcs} label={t("openNc")} sub={`${t("ncClosed")} ${k.closedNcs ?? 0}`} tone={k.openNcs ? "alert" : undefined} onClick={() => nav("/ncs")} />
        <Kpi value={k.overdueNcs ?? 0} label={t("ncOverdue")} tone={k.overdueNcs ? "alert" : undefined} onClick={() => nav("/ncs")} />
        <Kpi value={k.openActions} label={t("acOpen")} onClick={() => nav("/actions")} />
        <Kpi value={k.overdueActions} label={t("acOverdue")} tone={k.overdueActions ? "alert" : undefined} onClick={() => nav("/actions")} />
        <Kpi
          value={
            <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              {fmtN(k.avgScore, 1)} {k.avgScore != null && <Rating score={k.avgScore} />}
            </span>
          }
          label={t("avgRating")}
          onClick={() => nav("/suppliers")}
        />
        <Kpi value={k.capPct == null ? "—" : `${fmtN(k.capPct)}%`} label={t("overallCap")} />
        {showRisk && <Kpi value={k.coveragePct == null ? "—" : `${fmtN(k.coveragePct)}%`} label={t("a_coverageKpi")} onClick={() => nav("/plan")} />}
        <Kpi value={k.phaseOutSuppliers} label={t("a_phaseOutSuppliers")} tone={k.phaseOutSuppliers ? "alert" : undefined} onClick={() => nav("/suppliers")} />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t("ratingTrend")} extra={mobile ? null : <span style={{ fontSize: 12, opacity: 0.65 }}>{t("a_trendSub")}</span>}>
            <TrendChart data={d.scoreTrend} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={t("a_ratingDist")}>
            <RatingDistribution dist={d.ratingDistribution} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("capProgress")}>
            {capRows.length ? (
              capRows.map((s) => (
                <div key={s.id} style={{ marginBottom: 10 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 1.2fr 44px", gap: 10, alignItems: "center" }}>
                    <a onClick={() => nav(`/suppliers/${s.id}`)} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.name}>
                      {s.name}
                    </a>
                    <HBar pct={s.capPct ?? 0} tone={(s.capPct ?? 0) >= 80 ? "ok" : (s.capPct ?? 0) >= 40 ? "info" : "warn"} />
                    <span style={{ textAlign: "right", fontSize: 12 }}>{s.capPct}%</span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ opacity: 0.6 }}>{t("none")}</div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("a_ncBySeverity")}>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {(["obs", "minor", "major", "critical", "zero"] as const).map((s) => (
                <div key={s}>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>{k.openNcsBySeverity?.[s] ?? 0}</div>
                  <div style={{ fontSize: 12, opacity: 0.65 }}>{t(`sev_${s}`)}</div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("upcoming")} styles={{ body: { padding: 0 } }}>
            <ResponsiveList<AuditDto>
              size="small"
              rowKey="id"
              pageSize={false}
              dataSource={d.upcomingAudits}
              emptyText={t("none")}
              onRowClick={(r) => nav(`/audits/${r.id}`)}
              card={{
                code: (r) => r.code,
                title: (r) => r.supplierName,
                subtitle: (r) => (r.plannedDate ? fmtWhen(r) : t("dateHidden")),
                tags: (r) => <AuditStatusPill a={r} />,
              }}
              columns={[
                { title: t("code"), dataIndex: "code", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{v}</span> },
                { title: t("supplier"), dataIndex: "supplierName" },
                { title: t("plannedDate"), dataIndex: "plannedDate", render: (v: string | null, r: AuditDto) => (v ? fmtWhen(r) : t("dateHidden")) },
                { title: t("status"), render: (_: unknown, r: AuditDto) => <AuditStatusPill a={r} /> },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("overdueList")} styles={{ body: { padding: 0 } }}>
            <OverdueActions actions={d.overdueActionsList} onOpen={setActionId} />
          </Card>
        </Col>
        <Col xs={24}>
          <Card title={t("a_riskSuppliers")} styles={{ body: { padding: 0 } }}>
            <ResponsiveList<SupplierDto>
              size="small"
              rowKey="id"
              pageSize={false}
              dataSource={d.topRiskSuppliers}
              emptyText={t("none")}
              onRowClick={(r) => nav(`/suppliers/${r.id}`)}
              card={{
                code: (r) => r.code,
                title: (r) => r.name,
                tags: (r) => (
                  <>
                    {showRisk && <RiskPill r={r.risk} />}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 4 }}>
                      <Rating score={r.rating} override={r.ratingOverride} /> <span style={{ fontSize: 12, opacity: 0.65 }}>{fmtN(r.rating, 1)}</span>
                    </span>
                  </>
                ),
                fields: (r) => [
                  [t("openNc"), r.openNcs || "—"],
                  [t("capPct"), r.capPct == null ? "—" : `${r.capPct}%`],
                ],
              }}
              columns={[
                { title: t("sapCode"), dataIndex: "code", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{v}</span> },
                { title: t("name"), dataIndex: "name" },
                ...(showRisk ? [{ title: t("risk"), dataIndex: "risk", render: (v: string) => <RiskPill r={v} /> }] : []),
                {
                  title: t("rating"),
                  render: (_: unknown, r: SupplierDto) => (
                    <span>
                      <Rating score={r.rating} override={r.ratingOverride} /> <span style={{ fontSize: 12, opacity: 0.65 }}>{fmtN(r.rating, 1)}</span>
                    </span>
                  ),
                },
                { title: t("openNc"), dataIndex: "openNcs", align: "right" as const, render: (v?: number) => v || "—" },
                { title: t("capPct"), dataIndex: "capPct", align: "right" as const, render: (v?: number | null) => (v == null ? "—" : `${v}%`) },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <ActionDrawer actionId={actionId} onClose={() => setActionId(null)} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 12, opacity: 0.7 }}>{label}</span>
      {children}
    </div>
  );
}

/** Stato delle notifiche per gli admin: spente, oppure quanti eventi sono spenti. Apre Amministrazione → Notifiche. */
function NotifBadge({ cfg, onClick }: { cfg: ConfigDto["notifications"]; onClick: () => void }) {
  const { t } = useTranslation();
  const off = countDisabledEvents(cfg);
  if (cfg.enabled && !off) return null;
  return (
    <span role="button" tabIndex={0} data-testid="notif-badge" onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()} style={{ cursor: "pointer" }} title={t("cfgNotif")}>
      <Pill tone={cfg.enabled ? "warn" : "bad"}>{cfg.enabled ? t("n_stateSome", { n: off }) : t("n_stateOffShort")}</Pill>
    </span>
  );
}
